import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Card,
    Spinner,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell
} from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { PageHeader } from '@/components/layout';
import { useWeeklyReportsList } from '@/api/useWeeklyReports';
import { useTechnicalAssessmentsList } from '@/api/useTechnicalAssessments';
import { usePartsRequisitionsList } from '@/api/usePartsRequisitions';

import styles from './ReportsLibrary.module.css';

type ReportType = 'all' | 'weekly' | 'technical' | 'parts';

export function ReportsLibrary() {
    const navigate = useNavigate();
    const [reportType, setReportType] = useState<ReportType>('all');
    const [search, setSearch] = useState('');

    const { data: weeklyReports, isLoading: loadingWeekly } = useWeeklyReportsList();
    const { data: technicalAssessments, isLoading: loadingTechnical } = useTechnicalAssessmentsList();
    const { data: partsRequisitions, isLoading: loadingParts } = usePartsRequisitionsList();

    const isLoading = loadingWeekly || loadingTechnical || loadingParts;

    const allReports = useMemo(() => {
        const combined = [
            ...(weeklyReports?.map(r => ({
                id: r.id,
                type: 'weekly',
                label: 'Reporte Semanal',
                identifier: `RS-${r.id}`,
                date: r.week_start,
                status: r.status,
                technician: r.technician_name || 'N/A',
                viewPath: `/weekly-report/${r.id}/view`
            })) || []),
            ...(technicalAssessments?.map(r => ({
                id: r.id,
                type: 'technical',
                label: 'Evaluación Técnica',
                identifier: `AT-${r.id}`,
                date: r.date,
                status: r.status,
                technician: r.technician_name || 'N/A',
                viewPath: `/technical-assessment/${r.id}/view`
            })) || []),
            ...(partsRequisitions?.map(r => ({
                id: r.id,
                type: 'parts',
                label: 'Pedido de Repuestos',
                identifier: `REQ-${r.id}`,
                date: r.date,
                status: r.status,
                technician: r.requester_name || 'N/A',
                viewPath: `/parts-requisition/${r.id}/view`
            })) || [])
        ];

        return combined
            .filter(r => reportType === 'all' || r.type === reportType)
            .filter(r =>
                r.identifier.toLowerCase().includes(search.toLowerCase()) ||
                r.label.toLowerCase().includes(search.toLowerCase()) ||
                r.technician.toLowerCase().includes(search.toLowerCase())
            )
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [weeklyReports, technicalAssessments, partsRequisitions, reportType, search]);

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'submitted': return styles.statusSubmitted;
            case 'approved': return styles.statusApproved;
            case 'rejected': return styles.statusRejected;
            default: return styles.statusDraft;
        }
    };

    return (
        <div className={styles.container}>
            <PageHeader
                title="Biblioteca de Reportes"
                subtitle="Gestión y consulta de documentos históricos"
            />

            <Card variant="full" className={styles.filterCard}>
                <div className={styles.filterGrid}>
                    <Select
                        label="Tipo de Reporte"
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value as ReportType)}
                        options={[
                            { value: 'all', label: 'Todos los tipos' },
                            { value: 'weekly', label: 'Reportes Semanales' },
                            { value: 'technical', label: 'Evaluaciones Técnicas' },
                            { value: 'parts', label: 'Pedidos de Repuestos' }
                        ]}
                    />
                    <Input
                        name="search"
                        label="Buscar por ID o Técnico"
                        value={search}
                        setValue={setSearch}
                        placeholder="Escriba para filtrar..."
                    />
                </div>
            </Card>

            <Card variant="full" className={styles.tableCard}>
                {isLoading ? (
                    <div className={styles.loading}><Spinner size="lg" /></div>
                ) : (
                    <Table aria-label="Biblioteca de reportes">
                        <TableHeader>
                            <TableColumn>DOCUMENTO</TableColumn>
                            <TableColumn>IDENTIFICADOR</TableColumn>
                            <TableColumn>FECHA</TableColumn>
                            <TableColumn>TÉCNICO</TableColumn>
                            <TableColumn>ESTADO</TableColumn>
                            <TableColumn>ACCIONES</TableColumn>
                        </TableHeader>
                        <TableBody>
                            {allReports.map((report) => (
                                <TableRow key={`${report.type}-${report.id}`}>
                                    <TableCell>
                                        <div className={styles.reportTypeInfo}>
                                            <span className={`${styles.typeIcon} ${styles[report.type]}`}></span>
                                            {report.label}
                                        </div>
                                    </TableCell>
                                    <TableCell><span className={styles.identifier}>{report.identifier}</span></TableCell>
                                    <TableCell>{report.date}</TableCell>
                                    <TableCell>{report.technician}</TableCell>
                                    <TableCell>
                                        <span className={`${styles.statusBadge} ${getStatusStyle(report.status)}`}>
                                            {report.status === 'draft' ? 'Borrador' :
                                                report.status === 'submitted' ? 'Enviado' :
                                                    report.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <div className={styles.actionsCell}>
                                            <Button size="sm" variant="secondary" onClick={() => navigate(report.viewPath)}>
                                                <i className="fa-solid fa-eye"></i> Ver
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {!isLoading && allReports.length === 0 && (
                    <div className={styles.emptyState}>
                        <p>No se encontraron reportes con los criterios seleccionados.</p>
                    </div>
                )}
            </Card>
        </div>
    );
}
